'use client';

import { format } from 'date-fns';
import { HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { SourceCodeIcon } from '@terra/ui-icons';
import type { UnverifiedAssetDetailDto } from '@terra/shared-contracts-assets';
import { Badge } from '../../../components/badge';
import { Tooltip } from '../../../components/tooltip';
import {
  DrawerCustomSection,
  DrawerGeneralDetails,
  type DrawerDetailRow,
} from '../../custom-drawer';
import { UnverifiedAssetConclusion } from './UnverifiedAssetConclusion';
import { UnverifiedAssetProbeTrace } from './UnverifiedAssetProbeTrace';
import { resolveUnverifiedAssetOriginLabelKey } from './unverified-asset-origin.utils';

const DATE_FORMAT = 'MMM d, yyyy h:mm a';

type UnverifiedAssetDetailsContentProps = {
  detail: UnverifiedAssetDetailDto;
  variant?: 'page' | 'drawer';
};

export function UnverifiedAssetDetailsContent({
  detail,
  variant = 'page',
}: UnverifiedAssetDetailsContentProps) {
  const t = useTranslations('designSystem.applicationAssets');
  const tSections = useTranslations('designSystem.applicationAssets.sections');
  const hasProbeTrace = detail.unverifiedStatus !== 'BLOCKED';

  if (variant === 'drawer') {
    const originLabel = t(
      resolveUnverifiedAssetOriginLabelKey({ originKind: detail.origin.kind }),
    );
    const generalDetailsRows: DrawerDetailRow[][] = [
      [
        {
          title: tSections('discoveryDate'),
          value: format(new Date(detail.createdAt), DATE_FORMAT),
        },
        {
          title: tSections('lastUpdated'),
          value: format(new Date(detail.updatedAt), DATE_FORMAT),
        },
      ],
      [
        {
          title: t('origin'),
          value: (
            <Tooltip title={detail.origin.sourceDetail ?? originLabel}>
              <Badge size="sm" variant="outline" w="fit-content" minW="auto">
                <HStack gap="1" alignItems="center">
                  {detail.origin.kind === 'SOURCE_CODE' ? (
                    <SourceCodeIcon size={12} />
                  ) : null}
                  <span>{originLabel}</span>
                </HStack>
              </Badge>
            </Tooltip>
          ),
        },
      ],
    ];

    return (
      <VStack gap={8} alignItems="stretch" w="full">
        <DrawerGeneralDetails
          title={tSections('generalDetails')}
          rows={generalDetailsRows}
        />
        <DrawerCustomSection title={t('conclusion')}>
          <UnverifiedAssetConclusion
            conclusion={detail.conclusion}
            hideTitle
            variant="drawer"
          />
        </DrawerCustomSection>
        {hasProbeTrace ? (
          <DrawerCustomSection title={t('probeTrace')}>
            <UnverifiedAssetProbeTrace
              probeTrace={detail.probeTrace}
              hideTitle
            />
          </DrawerCustomSection>
        ) : null}
      </VStack>
    );
  }

  return (
    <VStack gap="6" alignItems="stretch" w="full" p="4">
      <UnverifiedAssetConclusion conclusion={detail.conclusion} />
      {hasProbeTrace ? (
        <UnverifiedAssetProbeTrace probeTrace={detail.probeTrace} />
      ) : null}
    </VStack>
  );
}
